'use client';

import type { ForwardedRef, ReactNode } from 'react';
import { forwardRef } from 'react';
import type {
  CardProps as MantineCardProps,
  CardSectionProps as MantineCardSectionProps,
} from '@mantine/core';
import { Card as MantineCard, CardSection as MantineCardSection } from '@mantine/core';

import type { LockedProps } from '../types';

export type CardProps = Omit<MantineCardProps, LockedProps>;

// Mantine's CardSectionProps doesn't declare `children` (CardProps does), so restate it.
type CardSectionProps = Omit<MantineCardSectionProps, LockedProps> & {
  children?: ReactNode;
};

const CardSection = (props: CardSectionProps) => {
  return <MantineCardSection {...props} />;
};

// Card tags its sections by matching child identity or this exact displayName. Without it a
// wrapped section never receives `data-orientation`, and every rule gated on it — the negative
// first/last margins and `inheritPadding` — silently does nothing.
CardSection.displayName = '@mantine/core/CardSection';

const CardRoot = forwardRef((props: CardProps, ref: ForwardedRef<HTMLDivElement>) => {
  return <MantineCard ref={ref} {...props} />;
});

CardRoot.displayName = 'Card';

type CardRootComponent = typeof CardRoot;

interface CardCompound extends CardRootComponent {
  /**
   * Use Card.Section to remove Card padding from its children while other elements still have horizontal spacing
   *
   * @example
   * <Card>
   *  <Card.Section>Content goes here</Card.Section>
   * </Card>
   */
  Section: typeof CardSection;
}

/**
 * ### Card
 *
 * @description
 * Mantine's Card minus per-call-site style overrides (see {@link LockedProps}). The surface itself
 * — elevated background, `xs` shadow, `md` radius, unclipped overflow, the draggable affordance —
 * lives in the theme's `Card` entry, so every card matches without per-instance styling. Style with
 * Mantine's own props (`padding`, `orientation`, `bg`, …); for a bespoke surface such
 * as the Highlighter App's gradient cards, use `Paper` instead.
 *
 * Two states the theme owns rather than bespoke props:
 * - **Validation error** — `mod={{ error: true }}` draws the red outline. Coerce a non-boolean to
 *   `!!` first, or the value lands in the DOM as `data-error="<your message>"`.
 * - **Disabled** — render `DisabledOverlay` as a child (it's absolutely positioned and z-indexed,
 *   so order doesn't matter; keep it outside any `Card.Section`). It also takes a `tooltip`.
 *
 * For the product-setup card with a label badge and "Apply to All", use `ProductCard` from
 * `@highlight-cp/core-ui-compositions`.
 *
 * @example
 * <Card>Content goes here</Card>
 * <Card mod={{ error: hasErrors }}>Content goes here</Card>
 * <Card>
 *  Content goes here
 *  {isReadOnly && <DisabledOverlay />}
 * </Card>
 * // Card.Section is OPTIONAL!
 * <Card>
 *  <Card.Section>
 *    remove Card padding from its children while other elements still have horizontal spacing
 *  </Card.Section>
 * </Card>
 *
 * @param props - {@link CardProps}
 * @returns {@link CardCompound}
 */
const Card = Object.assign(CardRoot, {
  Section: CardSection,
}) as CardCompound;

export default Card;
